import ApiBaseUrl from '../ApiBaseURL';
import { CLIENT_APP_HEADERS } from '../api/apiAccessGuard';

/**
 * Generate optimized image URL with resize parameters using the Image API
 * @param imagePath - The path to the image (e.g., "/uploads/items/item123.jpg")
 * @param width - Target width for resizing
 * @param height - Target height for resizing
 * @param quality - Image quality (1-100, default 85)
 * @returns Optimized image URL or null if no image path
 */
export const getOptimizedImageUrl = (
  imagePath: string | null | undefined,
  width?: number,
  height?: number,
  quality: number = 85
): string | null => {
  if (!imagePath || imagePath.trim() === '') {
    return null;
  }

  // Use the full API base URL for image requests
  const baseUrl = ApiBaseUrl.apiBaseUrl;
  const params = new URLSearchParams();
  
  if (width) params.append('width', width.toString());
  if (height) params.append('height', height.toString());
  params.append('quality', quality.toString());

  const queryString = params.toString();
  const finalUrl = `${baseUrl}/Image/resize${imagePath}${queryString ? `?${queryString}` : ''}`;
  
  // Debug logging
  console.log('Image URL generated:', {
    imagePath,
    baseUrl,
    width,
    height,
    quality,
    finalUrl
  });
  
  return finalUrl;
};

/**
 * Fetch authenticated image as blob and create object URL
 * This uses the same authentication method as other API calls
 * @param imagePath - The path to the image
 * @param width - Target width for resizing
 * @param height - Target height for resizing
 * @param quality - Image quality (1-100, default 85)
 * @returns Promise that resolves to object URL or null
 */
const fetchAuthenticatedImageUrl = async (
  imagePath: string | null | undefined,
  width?: number,
  height?: number,
  quality: number = 85
): Promise<string | null> => {
  if (!imagePath || imagePath.trim() === '') {
    return null;
  }

  const token = localStorage.getItem('authToken');
  if (!token) {
    console.warn('No auth token found for image request');
    return null;
  }

  try {
    const baseUrl = ApiBaseUrl.apiBaseUrl;
    const params = new URLSearchParams();
    
    if (width) params.append('width', width.toString());
    if (height) params.append('height', height.toString());
    params.append('quality', quality.toString());

    const queryString = params.toString();
    const url = `${baseUrl}/Image/resize${imagePath}?${queryString}`;
    
    // Debug logging
    console.log('Fetching authenticated image:', {
      imagePath,
      baseUrl,
      width,
      height,
      quality,
      hasToken: !!token,
      url
    });

    // Fetch image with Authorization header (same as apiFetch)
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...CLIENT_APP_HEADERS,
      },
    });

    if (!response.ok) {
      console.error('Image fetch failed:', response.status, response.statusText);
      return null;
    }

    // Convert blob to object URL
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    
    return objectUrl;
  } catch (error) {
    console.error('Error fetching authenticated image:', error);
    return null;
  }
};

// One request per (image, size) per session. The sale terminals mount hundreds of cards and remount
// them on every search or scan; each mount used to fetch the image again and create another object URL
// that nothing ever revoked, so a till left open all day grew by hundreds of MB.
const imageUrlCache = new Map<string, Promise<string | null>>();

export const getAuthenticatedImageUrl = (
  imagePath: string | null | undefined,
  width?: number,
  height?: number,
  quality: number = 85
): Promise<string | null> => {
  if (!imagePath || imagePath.trim() === '') return Promise.resolve(null);
  const key = `${imagePath}|${width ?? ''}|${height ?? ''}|${quality}`;
  const cached = imageUrlCache.get(key);
  if (cached) return cached;
  const pending = fetchAuthenticatedImageUrl(imagePath, width, height, quality)
    .then((url) => {
      if (!url) imageUrlCache.delete(key); // a failed load is not remembered: the next mount retries
      return url;
    })
    .catch((err) => {
      imageUrlCache.delete(key);
      throw err;
    });
  imageUrlCache.set(key, pending);
  return pending;
};

/** Releases every cached image (the object URLs are revoked). Called when the session ends. */
export const clearImageUrlCache = (): void => {
  for (const pending of imageUrlCache.values()) {
    pending.then((url) => { if (url) URL.revokeObjectURL(url); }).catch(() => undefined);
  }
  imageUrlCache.clear();
};

/**
 * Get default image URL for items/variants
 * @param type - Type of default image ('item' or 'variant')
 * @returns Default image URL
 */
export const getDefaultImageUrl = (type: 'item' | 'variant' = 'item'): string => {
  return '/icon-256x256.png'; // Default fallback image
};

/**
 * Get item image with fallback (async)
 * @param itemPhotoURL - Item photo URL
 * @param width - Target width
 * @param height - Target height
 * @returns Promise that resolves to optimized image URL or default
 */
export const getItemImageUrl = async (
  itemPhotoURL: string | null | undefined,
  width?: number,
  height?: number
): Promise<string> => {
  const optimizedUrl = await getAuthenticatedImageUrl(itemPhotoURL, width, height);
  return optimizedUrl || getDefaultImageUrl('item');
};

/**
 * Get variant image with fallback to item image (async)
 * @param variantPhotoURL - Variant photo URL
 * @param itemPhotoURL - Item photo URL (fallback)
 * @param width - Target width
 * @param height - Target height
 * @returns Promise that resolves to optimized image URL or default
 */
export const getVariantImageUrl = async (
  variantPhotoURL: string | null | undefined,
  itemPhotoURL: string | null | undefined,
  width?: number,
  height?: number
): Promise<string> => {
  // Try variant image first, then item image, then default
  const variantUrl = await getAuthenticatedImageUrl(variantPhotoURL, width, height);
  if (variantUrl) return variantUrl;
  
  const itemUrl = await getAuthenticatedImageUrl(itemPhotoURL, width, height);
  if (itemUrl) return itemUrl;
  
  return getDefaultImageUrl('variant');
};
